import { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';

import { deleteCodeUser } from '../../../api/Delete';
import { getWebappCode } from '../../../api/Get';
import { postCodeSeries } from '../../../api/Post';
import { putWebappCodeMetadata } from '../../../api/Put';
import { searchSerie } from '../../../api/Search';

import SeriePicker from './SeriePicker';

const { Paragraph, Text, Title } = Typography;

/*
 * Détail d'un code : ce qu'il ouvre, qui le détient, et comment faire évoluer sa sélection.
 *
 * Toute modification passe par une simulation : on voit ce que cela changerait pour les
 * détenteurs avant d'écrire. C'est la même logique que `npm run manage-code`.
 */
const CodeDetail = ({ code, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [mode, setMode] = useState('precis');
  const [precis, setPrecis] = useState({ addEpisodes: [], removeEpisodes: [] });
  const [toAdd, setToAdd] = useState([]);
  const [toRemove, setToRemove] = useState([]);
  const [exact, setExact] = useState([]);
  const [open, setOpen] = useState(true);
  const [revoke, setRevoke] = useState(false);

  const [options, setOptions] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    const res = await getWebappCode(code);
    if (res.error) setError(res.error);
    else {
      setDetail(res);
      setExact(current => (current.length > 0 ? current : res.code.series.map(s => s.id)));
    }
  }, [code]);

  useEffect(() => {
    setPlan(null);
    setToAdd([]);
    setToRemove([]);
    setExact([]);
    load();
  }, [load]);

  const searchSeries = async value => {
    if (!value) return;
    const res = await searchSerie(value);
    if (res && res.series) setOptions(res.series.map(s => ({ label: s.name, value: s.value ?? s.id })));
  };

  // La sélection envoyée au serveur, dans la forme qu'il attend.
  // La sélection envoyée au serveur, dans la forme qu'il attend.
  const selection = () => {
    if (mode === 'exact') return { series: exact };
    if (mode === 'precis') return precis;
    return { add: toAdd, remove: toRemove };
  };

  // Rien de coché ni de décoché : inutile d'interroger le serveur.
  const rienAFaire =
    (mode === 'precis' && precis.addEpisodes.length === 0 && precis.removeEpisodes.length === 0) ||
    (mode === 'ajuster' && toAdd.length === 0 && toRemove.length === 0) ||
    (mode === 'exact' && exact.length === 0);

  const simulate = async () => {
    setError('');
    setBusy(true);
    const res = await postCodeSeries(code, { dryRun: true, open, revoke, selection: selection() });
    setBusy(false);
    if (res.error) {
      setError(res.error);
      setPlan(null);
    } else setPlan(res.plan);
  };

  const apply = async () => {
    setError('');
    setBusy(true);
    const res = await postCodeSeries(code, { dryRun: false, open, revoke, selection: selection() });
    setBusy(false);
    if (res.error) setError(res.error);
    else {
      messageApi.open({ content: 'Sélection mise à jour', type: 'success' });
      setPlan(null);
      setToAdd([]);
      setToRemove([]);
      setPrecis({ addEpisodes: [], removeEpisodes: [] });
      load();
      if (onChanged) onChanged();
    }
  };

  const saveMeta = async values => {
    const res = await putWebappCodeMetadata(code, values);
    if (res.error) setError(res.error);
    else {
      messageApi.open({ content: 'Informations enregistrées', type: 'success' });
      load();
      if (onChanged) onChanged();
    }
  };

  const removeHolder = async (idUser, withRevoke) => {
    const res = await deleteCodeUser(code, idUser, { revoke: withRevoke });
    if (res.error) setError(res.error);
    else {
      messageApi.open({
        content: `Compte ${idUser} retiré${withRevoke ? ` (${res.repris} déblocages repris)` : ''}`,
        type: 'success',
      });
      load();
      if (onChanged) onChanged();
    }
  };

  if (!detail) return error ? <Alert message={error} showIcon type="error" /> : null;

  const seriesColumns = [
    { dataIndex: 'name', key: 'name', title: 'Série' },
    { dataIndex: 'episodes', key: 'episodes', title: 'Épisodes' },
    {
      key: 'actions',
      render: (_, row) => (
        <Button
          size="small"
          type="link"
          onClick={() => {
            setMode('ajuster');
            setToRemove(current => (current.includes(row.id) ? current : [...current, row.id]));
          }}>
          Retirer
        </Button>
      ),
      title: '',
    },
  ];

  const holderColumns = [
    { dataIndex: 'idUser', key: 'idUser', title: 'ID' },
    { dataIndex: 'mail', key: 'mail', title: 'Mail' },
    { dataIndex: 'role', key: 'role', title: 'Rôle' },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: d =>
        d ? (
          new Date(d).toLocaleString('fr-FR', {
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        ) : (
          <Text type="secondary">—</Text>
        ),
      // Les détenteurs arrivent déjà du plus récent au plus ancien.
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      title: 'Code utilisé le',
    },
    {
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Popconfirm title="Retirer le code à ce compte ?" onConfirm={() => removeHolder(row.idUser, false)}>
            <Button size="small" type="link">
              Retirer le code
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Retirer le code et reprendre les épisodes qu’il ouvrait ?"
            onConfirm={() => removeHolder(row.idUser, true)}>
            <Button danger size="small" type="link">
              Retirer et reprendre
            </Button>
          </Popconfirm>
        </Space>
      ),
      title: '',
    },
  ];

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {error && <Alert message={error} showIcon type="error" closable onClose={() => setError('')} />}

        <Row gutter={16}>
          <Col>
            <Statistic title="Épisodes" value={detail.code.episodes ?? detail.code.series.reduce((n, s) => n + s.episodes, 0)} />
          </Col>
          <Col>
            <Statistic title="Séries" value={detail.code.series.length} />
          </Col>
          <Col>
            <Statistic title="Détenteurs" value={detail.code.detenteurs} />
          </Col>
          <Col>
            <Statistic
              title="Créé le"
              value={detail.code.createdAt ? new Date(detail.code.createdAt).toLocaleDateString('fr-FR') : '—'}
            />
          </Col>
          <Col>
            <Statistic
              title="Modifié le"
              value={detail.code.updatedAt ? new Date(detail.code.updatedAt).toLocaleDateString('fr-FR') : '—'}
            />
          </Col>
        </Row>

        {detail.code.orphelins > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${detail.code.orphelins} épisode(s) rattaché(s) à ce code n’existent plus`}
            description="Ils n’apparaissent dans aucune série. Une sélection exacte les détachera."
          />
        )}

        <Form layout="inline" initialValues={detail.code} onFinish={saveMeta}>
          <Form.Item label="Description" name="description">
            <Input style={{ width: 280 }} />
          </Form.Item>
          <Form.Item label="Usages max" name="maxUsage">
            <InputNumber />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit">Enregistrer</Button>
          </Form.Item>
        </Form>

        <div>
          <Title level={5}>Séries ouvertes par ce code</Title>
          <Table columns={seriesColumns} dataSource={detail.code.series} pagination={false} rowKey="id" size="small" />
        </div>

        <div>
          <Title level={5}>Modifier la sélection</Title>
          <Paragraph type="secondary">
            Un ajustement ajoute ou retire des séries à ce qui existe. Une sélection exacte remplace la liste : tout ce qui n&apos;y
            figure pas est détaché.
          </Paragraph>

          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Radio.Group
              onChange={e => {
                setMode(e.target.value);
                setPlan(null);
              }}
              value={mode}>
              <Radio.Button value="precis">Série, saison ou épisode</Radio.Button>
              <Radio.Button value="ajuster">Séries entières</Radio.Button>
              <Radio.Button value="exact">Sélection exacte</Radio.Button>
            </Radio.Group>

            {mode === 'precis' && <SeriePicker code={code} onChange={setPrecis} />}

            {mode === 'precis' ? null : mode === 'ajuster' ? (
              <Space wrap align="start">
                <Space direction="vertical" size={2}>
                  <Text type="secondary">Séries à ajouter</Text>
                  <Select
                    mode="multiple"
                    filterOption={false}
                    onChange={setToAdd}
                    onSearch={searchSeries}
                    options={options}
                    placeholder="Rechercher une série"
                    style={{ width: 340 }}
                    value={toAdd}
                  />
                </Space>
                <Space direction="vertical" size={2}>
                  <Text type="secondary">Séries à retirer</Text>
                  <Select
                    mode="multiple"
                    onChange={setToRemove}
                    options={detail.code.series.map(s => ({ label: s.name, value: s.id }))}
                    placeholder="Parmi celles du code"
                    style={{ width: 340 }}
                    value={toRemove}
                  />
                </Space>
              </Space>
            ) : (
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary">Sélection complète</Text>
                <Select
                  mode="multiple"
                  filterOption={false}
                  onChange={setExact}
                  onSearch={searchSeries}
                  options={[...detail.code.series.map(s => ({ label: s.name, value: s.id })), ...options]}
                  placeholder="Les séries que le code doit ouvrir"
                  style={{ maxWidth: 700, width: '100%' }}
                  value={exact}
                />
              </Space>
            )}

            <Space direction="vertical" size={2}>
              <Text type="secondary">Effet sur les {detail.code.detenteurs} détenteur(s)</Text>
              <Space wrap>
                <Radio.Group onChange={e => setOpen(e.target.value)} value={open}>
                  <Radio value>Ouvrir les épisodes ajoutés</Radio>
                  <Radio value={false}>Ne pas les ouvrir</Radio>
                </Radio.Group>
                <Radio.Group onChange={e => setRevoke(e.target.value)} value={revoke}>
                  <Radio value={false}>Laisser les épisodes retirés acquis</Radio>
                  <Radio value>Reprendre les épisodes retirés</Radio>
                </Radio.Group>
              </Space>
            </Space>

            <Space>
              <Button disabled={rienAFaire} loading={busy} onClick={simulate}>
                Simuler
              </Button>
              <Button danger disabled={!plan} loading={busy} onClick={apply} type="primary">
                Appliquer
              </Button>
              {!plan && <Text type="secondary">Simuler d&apos;abord pour voir l&apos;effet.</Text>}
            </Space>

            {plan && (
              <Descriptions bordered column={2} size="small" title="Ce que la modification ferait">
                <Descriptions.Item label="Épisodes du code">
                  {plan.episodesAvant} → {plan.episodesApres}
                </Descriptions.Item>
                <Descriptions.Item label="Détenteurs">{plan.porteurs}</Descriptions.Item>
                <Descriptions.Item label="Séries ajoutées" span={2}>
                  {plan.seriesAjoutees.length > 0 ? (
                    <Space wrap>
                      {plan.seriesAjoutees.map(s => (
                        <Tag color="green" key={s.id ?? s.name}>
                          {s.name} ({s.count})
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">aucune</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Séries retirées" span={2}>
                  {plan.seriesRetirees.length > 0 ? (
                    <Space wrap>
                      {plan.seriesRetirees.map(s => (
                        <Tag color="red" key={s.id ?? s.name}>
                          {s.name} ({s.count})
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">aucune</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Déblocages ouverts">
                  {open ? plan.aOuvrir : `0 (${plan.aOuvrir} non ouverts)`}
                </Descriptions.Item>
                <Descriptions.Item label="Déblocages repris">
                  {revoke ? plan.aReprendre : `0 (${plan.aReprendre} laissés acquis)`}
                </Descriptions.Item>
                {revoke && plan.epargnes > 0 && (
                  <Descriptions.Item label="Épargnés" span={2}>
                    {plan.epargnes} — couverts par un autre code encore détenu
                  </Descriptions.Item>
                )}
                {/* Sur un changement fin, le décompte par série ne dit pas lesquels. */}
                {plan.attachIds.length > 0 && plan.attachIds.length <= 20 && (
                  <Descriptions.Item label="Épisodes ajoutés" span={2}>
                    <Space direction="vertical" size={0}>
                      {plan.episodesAjoutes.map(e => (
                        <Text key={e.id} type="secondary">
                          {`${e.serie} · S${e.saison}E${e.numero} — ${e.episode}`}
                        </Text>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
                {plan.detachIds.length > 0 && plan.detachIds.length <= 20 && (
                  <Descriptions.Item label="Épisodes retirés" span={2}>
                    <Space direction="vertical" size={0}>
                      {plan.episodesRetires.map(e => (
                        <Text key={e.id} type="secondary">
                          {`${e.serie || '(série inconnue)'} · S${e.saison}E${e.numero} — ${e.episode}`}
                        </Text>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
          </Space>
        </div>

        <div>
          <Title level={5}>Détenteurs ({detail.code.detenteurs})</Title>
          {detail.holders.length < detail.code.detenteurs && (
            <Paragraph type="secondary">Les {detail.holders.length} plus récents sont affichés.</Paragraph>
          )}
          <Table columns={holderColumns} dataSource={detail.holders} pagination={{ pageSize: 10 }} rowKey="idUser" size="small" />
        </div>
      </Space>
    </>
  );
};

export default CodeDetail;
